/**
 * Environment variables configuration
 * Centralized management of environment variables
 */

interface EnvConfig {
  // API Keys
  etherscanApiKey: string;
  apiKey: string;

  // Application Configuration
  appName: string;
  appVersion: string;
  environment: string;

  // External Services
  infuraProjectId?: string;
  alchemyApiKey?: string;

  // Analytics
  googleAnalyticsId?: string;
  mixpanelToken?: string;

  // Security & Features
  enableDebug: boolean;
  enableErrorReporting: boolean;
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
  const value = import.meta.env[key];
  return value || fallback;
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
  const value = getEnvVar(key);
  if (value === '') return fallback;
  return value.toLowerCase() === 'true';
}

/**
 * Environment configuration object
 */
export const envConfig: EnvConfig = {
  // API Keys
  etherscanApiKey: getEnvVar('VITE_ETHERSCAN_API_KEY', ''),
  apiKey: getEnvVar('VITE_API_KEY', 'your-api-key'),

  // Application Configuration
  appName: getEnvVar('VITE_APP_NAME', 'Titron'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  environment: getEnvVar('VITE_ENVIRONMENT', 'development'),

  // External Services
  infuraProjectId: getEnvVar('VITE_INFURA_PROJECT_ID'),
  alchemyApiKey: getEnvVar('VITE_ALCHEMY_API_KEY'),

  // Analytics
  googleAnalyticsId: getEnvVar('VITE_GOOGLE_ANALYTICS_ID'),
  mixpanelToken: getEnvVar('VITE_MIXPANEL_TOKEN'),

  // Security & Features
  enableDebug: getBooleanEnvVar('VITE_ENABLE_DEBUG', false),
  enableErrorReporting: getBooleanEnvVar('VITE_ENABLE_ERROR_REPORTING', true),
};

/**
 * Validate required environment variables
 */
export function validateEnvConfig(): { isValid: boolean; missing: string[] } {
  const missing: string[] = [];

  // Check required API keys
  if (!envConfig.etherscanApiKey) {
    missing.push('VITE_ETHERSCAN_API_KEY');
  }

  // In production, require more strict validation
  if (envConfig.environment === 'production') {
    if (!envConfig.infuraProjectId && !envConfig.alchemyApiKey) {
      missing.push('VITE_INFURA_PROJECT_ID or VITE_ALCHEMY_API_KEY');
    }
  }

  return {
    isValid: missing.length === 0,
    missing,
  };
}

/**
 * Log environment configuration (development only)
 */
export function logEnvConfig(): void {
  if (envConfig.enableDebug && envConfig.environment === 'development') {
    console.group('🔧 Environment Configuration');
    console.log('App Name:', envConfig.appName);
    console.log('Version:', envConfig.appVersion);
    console.log('Environment:', envConfig.environment);
    console.log('Debug Enabled:', envConfig.enableDebug);
    console.log('Error Reporting:', envConfig.enableErrorReporting);
    console.log('Etherscan API Key Configured:', !!envConfig.etherscanApiKey);
    console.log('Analytics Configured:', !!envConfig.googleAnalyticsId);
    console.groupEnd();
  }
}
